import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Button, Table, Form, InputGroup, Badge, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import supabase from '../../../supabaseClient';

function BlogManagement() {
  const [blogs, setBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Date Filtering & 20 items per page
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; 

  const fetchBlogs = useCallback(async () => {
    let { data: blogsData, error } = await supabase
      .from('blogs')
      .select('*')
      .order('date', { ascending: false }); // Ordered by date matching your table schema
    
    if (error) {
      console.error('Error fetching blogs:', error);
    } else {
      setBlogs(blogsData || []);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const deleteBlog = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this blog post?");
    if (!confirmDelete) return;

    let { error } = await supabase
      .from('blogs')
      .delete()
      .eq('id', id);
      
    if (error) console.error('Error deleting blog:', error);
    else fetchBlogs();
  };

  // 1. Core Feature: Filter by Search Term AND Date Range
  const filteredBlogs = blogs.filter(blog => {
    // Text search matching
    const matchesSearch = 
      blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.author?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // FIX: Using blog.date directly from your database values
    if (blog.date) {
      // Parse the blog date safely
      const blogDate = new Date(blog.date).setHours(0,0,0,0);
      
      if (startDate) {
        const start = new Date(startDate).setHours(0,0,0,0);
        if (blogDate < start) return false;
      }
      
      if (endDate) {
        const end = new Date(endDate).setHours(23,59,59,999);
        if (blogDate > end) return false;
      }
    } else if (startDate || endDate) {
      // If blog has no date field but a date filter is selected, exclude it
      return false;
    }

    return true;
  });

  // 2. Core Feature: Pagination Calculations (20 per page)
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  // Clear all filters easily
  const resetFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  return (
    <Container className="py-5 blog-management" data-aos="fade-up">
      {/* Header Section */}
      <Row className="align-items-center mb-4">
        <Col md={6}>
          <h2 className="fw-bold text-dark m-0">Blog Dashboard</h2>
          <p className="text-muted small mb-0">Manage, edit, and publish your website content.</p>
        </Col>
        <Col md={6} className="text-md-end mt-3 mt-md-0">
          <Link to="/blog/new" className="btn btn-primary px-4 py-2 shadow-sm fw-semibold">
            + Add New Blog
          </Link>
        </Col>
      </Row>

      {/* Metrics Cards */}
      <Row className="g-3 mb-4">
        <Col xs={6} sm={3}>
          <div className="p-3 bg-white border rounded shadow-sm">
            <small className="text-muted d-block text-uppercase fw-bold">Total Posts</small>
            <span className="fs-3 fw-bold">{blogs.length}</span>
          </div>
        </Col>
        <Col xs={6} sm={3}>
          <div className="p-3 bg-white border rounded shadow-sm">
            <small className="text-muted d-block text-uppercase fw-bold">Filtered Count</small>
            <span className="fs-3 fw-bold text-primary">{filteredBlogs.length}</span>
          </div>
        </Col>
      </Row>

      {/* Advanced Filter Toolbar (Search + Date Range) */}
      <Row className="g-3 mb-4 align-items-end">
        <Col md={4}>
          <Form.Group>
            <Form.Label className="small fw-semibold text-muted">Search</Form.Label>
            <Form.Control
              placeholder="Search title or author..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col sm={6} md={3}>
          <Form.Group>
            <Form.Label className="small fw-semibold text-muted">From Date</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col sm={6} md={3}>
          <Form.Group>
            <Form.Label className="small fw-semibold text-muted">To Date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col sm={12} md={2} className="text-md-end">
          {(searchTerm || startDate || endDate) && (
            <Button variant="outline-secondary" className="w-100" onClick={resetFilters}>
              Clear Filters
            </Button>
          )}
        </Col>
      </Row>

      {/* Main Table Layout */}
      <div className="bg-white border rounded shadow-sm overflow-hidden">
        <Table responsive hover className="align-middle mb-0">
          <thead className="table-light text-uppercase fs-7 text-muted">
            <tr>
              <th className="px-4 py-3" style={{ width: '80px' }}>Srl No</th>
              <th className="py-3" style={{ width: '35%' }}>Title</th>
              <th className="py-3">Author</th>
              <th className="py-3">Date</th>
              <th className="py-3">Status</th>
              <th className="px-4 py-3 text-end" style={{ width: '180px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentBlogs.length > 0 ? (
              currentBlogs.map((blog, index) => {
                const serialNumber = indexOfFirstItem + index + 1;

                return (
                  <tr key={blog.id}>
                    <td className="px-4 py-3 text-muted fw-medium">{serialNumber}</td>
                    <td className="py-3 fw-semibold text-dark">{blog.title}</td>
                    {/* FIX: Restored missing Author column element */}
                    <td className="text-muted">{blog.author || 'Anonymous'}</td>
                    <td className="text-muted small">
                      {blog.date ? new Date(blog.date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td>
                      <Badge bg={blog.status === 'Draft' ? 'secondary' : 'success'}>
                        {blog.status || 'Published'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <Link to={`/blog/edit/${blog.id}`} className="btn btn-outline-warning btn-sm fw-medium">
                          Edit
                        </Link>
                        <Button variant="outline-danger" size="sm" className="fw-medium" onClick={() => deleteBlog(blog.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-5 text-muted">
                  No blogs match your filter criteria.
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {/* Pagination Section */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center px-4 py-3 bg-light border-top">
            <span className="small text-muted">
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredBlogs.length)} of {filteredBlogs.length} posts
            </span>
            <Pagination className="mb-0">
              <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />
              {[...Array(totalPages)].map((_, index) => (
                <Pagination.Item 
                  key={index + 1} 
                  active={index + 1 === currentPage}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} />
            </Pagination>
          </div>
        )}
      </div>
    </Container>
  );
}

export default BlogManagement;